import { type MailRibbonControlId } from "owa-ribbon-ids/lib/mailRibbonId";
import { isMonarchMultipleAccountsEnabled } from "owa-account-source-list";
import { lazyShowCategoryDialog } from "owa-categories";
import setInitialCategoryDialogViewState from "owa-categories/lib/actions/setInitialCategoryDialogViewState";
import type CategoryActionSource from "owa-categories/lib/utils/CategoryActionSource";
import type { MailboxInfo } from "owa-client-types";
import { getActionSourceFromCommandingViewMode } from "owa-command-ribbon";
import { lazyAddFavoriteCategory } from "owa-favorites";
import { resetFocus } from "owa-mail-focus-manager";
import { getSelectedTableView, tryGetMailboxInfoFromRowKeysOrIdsIfUnifiedInboxOrDefault } from "owa-mail-list-store";
import getEffectiveRowKeys from "owa-mail-list-store/lib/utils/getEffectiveRowKeys";
import { getMailboxInfo } from "owa-mail-mailboxinfo";
import { lazyAddCategoriesFromTable, lazyClearCategoriesFromTable, lazyRemoveCategoriesFromTable, lazyAddCategoriesFromItem, lazyClearCategoriesFromItem, lazyRemoveCategoriesFromItem, } from "owa-mail-triage-action";
import { lazyMountAndShowFullOptions } from "owa-options-view";
import type { ClientItemId } from "owa-client-ids";
import { isGlobalFeatureEnabled } from "owa-feature-flags";
import { getReadRibbonId } from "owa-mail-ribbon-utils/lib/getReadRibbonId";
import setCategoryMenuFindText from "owa-categories/lib/actions/setCategoryMenuFindText";
import setShouldShowAllCategories from "owa-categories/lib/actions/setShouldShowAllCategories";
import { logUsage } from "owa-analytics";
import { type UTCalendarEventFeatureAction, type UTCalendarUIContext, type UTCalendarOrigin } from "@outlook/unified_telemetry";
import { getMailCategorizeCallbacks } from "owa-calendar-unified-telemetry-utils/lib/utils/logMailCategorizeTelemetry";
import { getRibbonViewMode } from "owa-command-ribbon-store";
import { type CommandingViewMode } from "owa-outlook-service-option-store/lib/store/schema/options/CommandingOptions";
import { isPopout } from "owa-popout-v2";
import { isMeetingMessage } from "owa-meeting-message-utils/lib/utils/isMeetingMessage";
import { getSingleSelectedItemFromView } from "./getSelectedItemFromView";
import { getStore as getMailStore } from "owa-mail-store/lib/store/Store";
import getActiveSecondaryReadingPaneTabItemId from "owa-tab-store/lib/utils/getActiveSecondaryReadingPaneTabItemId";
import updateSecondaryTabCategoriesMutator from "owa-tab-store/lib/actions/updateSecondaryTabCategoriesMutator";

/**
 * Wrapper function that handles item lookup, meeting message checks, and telemetry logging for mail category actions
 * @param action The category action being performed
 * @param itemId Optional item ID to target specific item
 */
function logCategorizeUnifiedTelemetry(action: UTCalendarEventFeatureAction, itemId?: ClientItemId): void {
    let uiContext: UTCalendarUIContext;
    let currentItem;
    
    const ribbonViewMode = getRibbonViewMode();
    const origin: UTCalendarOrigin = ribbonViewMode === 1
        ? "SimplifiedRibbon" : "ClassicRibbon";
    
    if (isPopout(window)) {
        uiContext = "MailReadFormPopout";
    }
    else {
        uiContext = "MailList";
    }
    
    if (itemId) {
        currentItem = getMailStore().items.get(itemId.Id);
    }
    else {
        currentItem = getSingleSelectedItemFromView(getSelectedTableView());
    }
    
    // Only log telemetry for meeting messages
    if (currentItem && isMeetingMessage(currentItem)) {
        const { onSuccess } = getMailCategorizeCallbacks(action, uiContext, origin);
        onSuccess();
    }
}

/**
 * Function to handle a category clicked.
 *
 * @param categoryName: name of category
 * @param isChecked: whether category is currently applied on the item(s)
 *
 */
export const onCategoryClicked = (categoryName: string, isChecked: boolean, itemId: ClientItemId | undefined, mailboxInfo: MailboxInfo | undefined): void => {
    const tableView = getSelectedTableView(true /* shouldGetEffectiveTableView */);
    if (!tableView) {
        return;
    }
    const effectiveRowKeys = getEffectiveRowKeys(tableView);
    // Resolve mailboxInfo at execution time — not from computed/cached values —
    // because the user may have switched accounts after the menu rendered.
    // getCategorizeSections.ts resolves separately at render time to load
    // the correct account's category list; handlers must re-resolve here.
    mailboxInfo =
        itemId?.mailboxInfo ??
            tryGetMailboxInfoFromRowKeysOrIdsIfUnifiedInboxOrDefault(getMailboxInfo(tableView), effectiveRowKeys, tableView);
    
    // Keep the ribbon categorize menu open when multi-select is enabled; otherwise dismiss.
    if (!isGlobalFeatureEnabled("cal-multiSelectCategory")) {
        resetFocus("ribbonCategoryMenuDismissed");
    }
    
    // Get telemetry callbacks for category action
    const action = isChecked
        ? "UnselectItem" : "SelectItem";
    logCategorizeUnifiedTelemetry(action, itemId);
    
    if (itemId !== undefined) {
        if (isChecked) {
            lazyRemoveCategoriesFromItem.importAndExecute(itemId.Id, mailboxInfo, [categoryName], getActionSourceFromCommandingViewMode());
        }
        else {
            lazyAddCategoriesFromItem.importAndExecute(itemId.Id, mailboxInfo, [categoryName], undefined, getActionSourceFromCommandingViewMode());
        }
    }
    else {
        if (isChecked) {
            // Checked category is clicked, remove category.
            lazyRemoveCategoriesFromTable.importAndExecute(effectiveRowKeys, tableView, [categoryName], getActionSourceFromCommandingViewMode());
        }
        else {
            // Unchecked category is clicked, add category.
            lazyAddCategoriesFromTable.importAndExecute(effectiveRowKeys, tableView, [categoryName], getActionSourceFromCommandingViewMode());
        }
        
        // Update the secondary tab's category snapshot so the reading pane header re-renders.
        if (getActiveSecondaryReadingPaneTabItemId()) {
            updateSecondaryTabCategoriesMutator([categoryName], !isChecked /* isAdding */, false /* clearCategories */);
        }
    }
};

/**
 * Shows the Category Dialog to allow user to create a new category
 */
export const onNewCategoryClicked = (tabId: string | undefined, windowRetriever: (tabId: string | undefined) => Window, categoryName: string | undefined, itemId: ClientItemId | undefined): void => {
    logCategorizeUnifiedTelemetry("New", itemId);
    const tableView = getSelectedTableView(true /* shouldGetEffectiveTableView */);
    if (!tableView) {
        return;
    }
    const effectiveRowKeys = getEffectiveRowKeys(tableView);
    // Resolve mailboxInfo at execution time — not from computed/cached values —
    // because the user may have switched accounts after the menu rendered.
    // getCategorizeSections.ts resolves separately at render time to load
    // the correct account's category list; handlers must re-resolve here.
    const mailboxInfo = itemId?.mailboxInfo ??
        tryGetMailboxInfoFromRowKeysOrIdsIfUnifiedInboxOrDefault(getMailboxInfo(tableView), effectiveRowKeys, tableView);
    
    const targetWindow = windowRetriever(tabId);
    
    const addFavoriteCategoryCallback = (categoryId: string, actionSource: CategoryActionSource, favoriteMailboxInfo: MailboxInfo) => {
        lazyAddFavoriteCategory
            .import()
            .then(addFavoriteCategory => addFavoriteCategory(categoryId, actionSource, favoriteMailboxInfo));
    };
    
    const onAfterCreateNewCategory = (category?: string, createNewCategoryPromise?: Promise<boolean>) => {
        // When a new category is created, we should apply it optimistically to the selected items
        if (itemId) {
            lazyAddCategoriesFromItem.importAndExecute(itemId.Id, mailboxInfo, category ? [category] : [], undefined, getActionSourceFromCommandingViewMode());
        }
        else if (effectiveRowKeys.length > 0) {
            lazyAddCategoriesFromTable.importAndExecute(effectiveRowKeys, tableView, category ? [category] : [], getActionSourceFromCommandingViewMode(), createNewCategoryPromise);
        }
        
        // Update the secondary tab header pills regardless of itemId vs table path
        if (getActiveSecondaryReadingPaneTabItemId() && category) {
            updateSecondaryTabCategoriesMutator([category], true /* isAdding */, false /* clearCategories */);
        }
    };
    
    setInitialCategoryDialogViewState("new", categoryName);
    lazyShowCategoryDialog.importAndExecute({
        actionSource: getActionSourceFromCommandingViewMode(),
        addFavoriteCategory: addFavoriteCategoryCallback,
        onAfterCreateNewCategory,
        mailboxInfo,
        targetWindow,
    });
};

/**
 * Clears all categories on the selected mail item(s)
 */
export const onClearCategoriesClicked = (itemId: ClientItemId | undefined): void => {
    const tableView = getSelectedTableView(true /* shouldGetEffectiveTableView */);
    if (!tableView) {
        return;
    }
    const effectiveRowKeys = getEffectiveRowKeys(tableView);
    // Resolve mailboxInfo at execution time — not from computed/cached values —
    // because the user may have switched accounts after the menu rendered.
    // getCategorizeSections.ts resolves separately at render time to load
    // the correct account's category list; handlers must re-resolve here.
    const mailboxInfo = itemId?.mailboxInfo ??
        tryGetMailboxInfoFromRowKeysOrIdsIfUnifiedInboxOrDefault(getMailboxInfo(tableView), effectiveRowKeys, tableView);
    resetFocus("ribbonClearCategoriesClicked");
    
    logCategorizeUnifiedTelemetry("Clear", itemId);
    
    if (itemId) {
        lazyClearCategoriesFromItem.importAndExecute(itemId.Id, mailboxInfo);
    }
    else {
        lazyClearCategoriesFromTable.importAndExecute(effectiveRowKeys, tableView, getActionSourceFromCommandingViewMode());
        
        if (getActiveSecondaryReadingPaneTabItemId()) {
            updateSecondaryTabCategoriesMutator([] /* categories */, false /* isAdding */, true /* clearCategories */);
        }
    }
};

/**
 * Shows the manage categories dialog
 */
export const onManageCategoriesClicked = (itemId: ClientItemId | undefined): void => {
    const tableView = getSelectedTableView(true /* shouldGetEffectiveTableView */);
    if (!tableView) {
        return;
    }
    const effectiveRowKeys = getEffectiveRowKeys(tableView);
    // Resolve mailboxInfo at execution time — not from computed/cached values —
    // because the user may have switched accounts after the menu rendered.
    // getCategorizeSections.ts resolves separately at render time to load
    // the correct account's category list; handlers must re-resolve here.
    const effectiveMailboxInfo = itemId?.mailboxInfo ??
        tryGetMailboxInfoFromRowKeysOrIdsIfUnifiedInboxOrDefault(getMailboxInfo(tableView), effectiveRowKeys, tableView);
    
    logUsage("TnS_Category_ManageCategoriesRibbon", effectiveMailboxInfo);
    
    logCategorizeUnifiedTelemetry("Manage", itemId);
    
    if (isMonarchMultipleAccountsEnabled() || isGlobalFeatureEnabled("acct-multiacctcomingsoon")) {
        lazyMountAndShowFullOptions.importAndExecute("accounts-category", "categories", undefined, undefined, effectiveMailboxInfo);
    }
    else {
        lazyMountAndShowFullOptions.importAndExecute("accounts-category", "categories", undefined /* option */, undefined /* targetWindow */, effectiveMailboxInfo);
    }
};

export const onAllCategoriesClicked = (itemId: ClientItemId | undefined): void => {
    const tableView = getSelectedTableView(true /* shouldGetEffectiveTableView */);
    if (!tableView) {
        return;
    }
    const effectiveRowKeys = getEffectiveRowKeys(tableView);
    // Resolve mailboxInfo at execution time — not from computed/cached values —
    // because the user may have switched accounts after the menu rendered.
    // getCategorizeSections.ts resolves separately at render time to load
    // the correct account's category list; handlers must re-resolve here.
    const mailboxInfo = itemId?.mailboxInfo ??
        tryGetMailboxInfoFromRowKeysOrIdsIfUnifiedInboxOrDefault(getMailboxInfo(tableView), effectiveRowKeys, tableView);
    logUsage("TnS_Category_AllCategoriesRibbon", mailboxInfo);
    const ele: HTMLElement | null = document.getElementById(getReadRibbonId(626, itemId).toString());
    
    if (ele) {
        ele.focus();
    }
    
    resetCategorizeSearch(true /*showAllCategories*/);
};

export const resetCategorizeSearch = (showAllCategories: boolean | undefined): void => {
    setCategoryMenuFindText("");
    setShouldShowAllCategories(showAllCategories ?? false);
};
